import React from 'react';

import { TicketStatus } from '../../../Api/CRM/Responses/LotteryTicketResponse';
import LotteryTicket from '../../../Api/Models/LotteryTicket';
import { Symbols } from '../../../utils/Symbols';
import * as formatters from '../../../utils/formatters';
import ActionButton from '../../elements/ActionButton';
import ProtectedLink from '../../elements/ProtectedLink';
import ColoredStatus from '../../elements/common/ColoredStatus';
import SortButton, { SortProps } from '../../elements/common/SortButton';
import { TableCell as Cell } from '../../elements/common/TableDetailsRow';
import TableHead from '../../elements/common/TableHead';

import { PERMISSION } from '../../helpers/Permissions';

import * as template from 'utils/template';

type Props = Readonly<{
    tickets: LotteryTicket[];
    sortProps: SortProps;
}>;

function TicketsTableDefault({ tickets, sortProps }: Props) {
    const isUserDetails = window.location.pathname.includes('users') || window.location.pathname.includes('resellers');
    const { formatDateStringWithTimezone, formatCurrency } = formatters;

    const ticketList = tickets.map((ticket) => (
        <tr key={ticket.id}>
            <td>
                <ActionButton title="Open" url={`/tickets/${ticket.id}`} permissions={[PERMISSION.VIEW_TICKET]} />{' '}
                {canDisplayAsUserButton(ticket.status) && (
                    <ActionButton
                        title="Display"
                        url={`/tickets/user_view/${ticket.id}`}
                        permissions={[PERMISSION.VIEW_TICKET]}
                    />
                )}
            </td>
            <Cell>{ticket.token}</Cell>
            {!isUserDetails && [
                <td key="email">
                    <ProtectedLink
                        url={`/users/${ticket.user_id}`}
                        value={ticket.email}
                        permissions={[PERMISSION.VIEW_USER]}
                    />
                </td>,
                <Cell key="phone">{ticket.phone}</Cell>,
            ]}
            <td>
                <ProtectedLink
                    url={`/lotteries/${ticket.lottery_slug}`}
                    value={ticket.lottery_name}
                    permissions={[PERMISSION.VIEW_LOTTERY]}
                />
            </td>
            <Cell>
                <ColoredStatus status={ticket.status} />
            </Cell>
            <Cell>{formatCurrency(ticket.system_sell_amount_gross)}</Cell>
            <Cell>
                {ticket.ticket_markup
                    ? formatCurrency(ticket.ticket_markup, 2, ticket.system_sell_amount_currency_code)
                    : Symbols.EM_DASH}
            </Cell>
            <Cell>{formatDateStringWithTimezone(ticket.draw_date)}</Cell>
            <Cell>{ticket.systemPrizeGross ? ticket.systemPrizeGross.formatted : Symbols.EM_DASH}</Cell>
            <Cell>{formatDateStringWithTimezone(ticket.created_at)}</Cell>
        </tr>
    ));

    return (
        <table className="table model-list">
            <TableHead>
                <th>Action</th>
                <SortButton column="token" {...sortProps} />
                {!isUserDetails && <SortButton column="users.email" label="Email" {...sortProps} />}
                {!isUserDetails && <SortButton column="users.phone" label="Phone" {...sortProps} />}
                <SortButton column="lotteries.name" label="Lottery" {...sortProps} />
                <SortButton column="status" {...sortProps} />
                <SortButton column="system_sell_amount_gross" label="Amount" {...sortProps} />
                <SortButton column="system_sell_amount_gross" label="Markup" {...sortProps} />
                <SortButton column="draw_date" {...sortProps} />
                <SortButton column="prize_gross" label="Prize" {...sortProps} />
                <SortButton column="created_at" order="desc" {...sortProps} />
            </TableHead>
            <tbody>{ticketList}</tbody>
        </table>
    );
}

function canDisplayAsUserButton(status: TicketStatus) {
    if (status !== TicketStatus.WON) {
        return false;
    }

    return template.isZambia() || template.isNigeria() || template.isLottoPark();
}

export default TicketsTableDefault;
